import { useState } from 'react';
import { Bookmark, Plus, Trash2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { apiClient } from '@/api/client';
import { SavedView, ViewConfig } from '@/lib/queryable/types';

interface SavedViewPickerProps {
  surfaceKey: string;
  savedViews: SavedView[];
  onLoad: (view: SavedView) => void;
  currentViewConfig: ViewConfig;
}

export function SavedViewPicker({
  surfaceKey,
  savedViews,
  onLoad,
  currentViewConfig,
}: SavedViewPickerProps) {
  const [saveOpen, setSaveOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const qc = useQueryClient();

  const createMutation = useMutation({
    mutationFn: async (name: string) => {
      const { data } = await apiClient.post('/views', {
        surfaceKey,
        name,
        viewConfig: currentViewConfig,
        isDefault: false,
      });
      return data.data as SavedView;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['saved-views', surfaceKey] });
      setSaveOpen(false);
      setViewName('');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (viewId: string) => {
      await apiClient.delete(`/views/${viewId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['saved-views', surfaceKey] });
    },
  });

  const setDefaultMutation = useMutation({
    mutationFn: async (viewId: string) => {
      await apiClient.post(`/views/${viewId}/set-default`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['saved-views', surfaceKey] });
    },
  });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="h-8 gap-1.5 text-xs">
            <Bookmark className="h-3 w-3" />
            Views
            {savedViews.length > 0 && (
              <span className="ml-1 text-text-secondary">({savedViews.length})</span>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {savedViews.length > 0 && (
            <>
              <DropdownMenuLabel className="text-xs text-text-secondary">Saved views</DropdownMenuLabel>
              <DropdownMenuGroup>
                {savedViews.map((view) => (
                  <DropdownMenuItem
                    key={view.id}
                    className="flex items-center justify-between group"
                    onSelect={(e) => e.preventDefault()}
                  >
                    <button
                      className="flex-1 text-left text-sm"
                      onClick={() => onLoad(view)}
                    >
                      {view.isDefault && <span className="mr-1 text-accent-cyan">★</span>}
                      {view.name}
                    </button>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {!view.isDefault && (
                        <button
                          title="Set as default"
                          onClick={() => setDefaultMutation.mutate(view.id)}
                          className="p-0.5 hover:text-accent-cyan"
                        >
                          <Bookmark className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        title="Delete view"
                        onClick={() => deleteMutation.mutate(view.id)}
                        className="p-0.5 hover:text-accent-rose"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    </div>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuGroup>
              <DropdownMenuSeparator />
            </>
          )}
          <DropdownMenuItem onClick={() => setSaveOpen(true)}>
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Save current view
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="sm:max-w-xs">
          <DialogHeader>
            <DialogTitle>Save view</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <Input
              placeholder="View name"
              value={viewName}
              onChange={(e) => setViewName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && viewName.trim()) createMutation.mutate(viewName.trim());
              }}
              autoFocus
            />
            <Button
              className="w-full"
              disabled={!viewName.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate(viewName.trim())}
            >
              Save
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
